import React from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import {
  Text,
  Picture,
  ShareButton
} from '@comicrelief/component-library';
import { getImage } from 'gatsby-plugin-image';

import Layout from '../../components/Layout/Layout';
import { SEO, SEO_PAGE_TYPES } from '../../components/SEO/SEO';
import renderRichText from '../../components/RichText/RichText';
import NewsBlock from '../../components/NewsBlock/NewsBlock';
import Breadcrumb from '../../components/Breadcrumb/Breadcrumb';
import ImagePropType from '../../commonPropTypes/commonPropTypes';

import {
  Wrapper, Container, Header, Image
} from './Article.styles';

const Article = ({ data }) => {
  const {
    article: { edges },
    related: { edges: relatedArticles }
  } = data;

  const post = edges[0].node;
  const {
    title, displayDate, image, body
  } = post;

  const thisImageData = getImage(image);
  const thisImageFallback = thisImageData?.images?.fallback?.src;
  const thisImageSrcSet = thisImageData?.images?.fallback?.srcSet
                     || thisImageData?.images?.sources?.[0]?.srcSet;
  const thisImageLow = thisImageData?.placeholder?.fallback;
  const thisImageDesc = image?.description ?? '';

  // As ALL news articles are 'children' of the /news page, we'll just hardcode this,
  // rather than using a per-article field set in the CMS:
  const parentPages = [
    { title: 'About Us', path: 'about' },
    { title: 'News', path: 'news' }
  ];

  return (
    <Layout>
      <Wrapper>

        <Breadcrumb
          thisPageTitle={title}
          parentPages={parentPages}
        />

        <Header>
          <Text tag="h1">
            {title}
          </Text>
          <Text tag="p">
            {displayDate}
          </Text>
        </Header>
        <Container>
          <div data-test="social-share">
            <ShareButton />
          </div>
          {image ? (
            <Image>
              <Picture
                alt={thisImageDesc}
                image={thisImageFallback}
                images={thisImageSrcSet}
                imageLow={thisImageLow}
              />
            </Image>
          ) : null}
          {body ? renderRichText(body, 'white', 'news') : null}
        </Container>
      </Wrapper>
      <section data-test="related-news">
        <NewsBlock data={relatedArticles} relatedArticlesRow />
      </section>
    </Layout>
  );
};

Article.propTypes = {
  data: PropTypes.shape({
    article: PropTypes.shape({
      edges: PropTypes.arrayOf(
        PropTypes.shape({
          node: PropTypes.shape({
            body: PropTypes.shape({
              raw: PropTypes.string
            }),
            displayDate: PropTypes.string.isRequired,
            image: ImagePropType,
            path: PropTypes.string.isRequired,
            title: PropTypes.string.isRequired,
            summary: PropTypes.shape({
              summary: PropTypes.string
            })

          })
        })
      )
    }).isRequired,
    related: PropTypes.shape({
      edges: PropTypes.arrayOf(
        PropTypes.shape({
          node: PropTypes.shape({
            body: PropTypes.shape({
              raw: PropTypes.string
            }),
            displayDate: PropTypes.string.isRequired,
            image: ImagePropType,
            path: PropTypes.string.isRequired,
            title: PropTypes.string.isRequired,
            summary: PropTypes.shape({
              summary: PropTypes.string
            })

          })
        })
      )
    }).isRequired
  }).isRequired
};

export default Article;

export const Head = props => (
  <SEO
    allData={props}
    pageType={SEO_PAGE_TYPES.ARTICLE}
    listAsArticle
  >
    {(siteUrl, linkedDataObj) => (
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Article',
          author: 'Comic Relief',
          headline: linkedDataObj.title,
          description: linkedDataObj.description,
          datePublished: linkedDataObj.articleDate,
          mainEntityOfPage: linkedDataObj.url,
          image: {
            '@type': 'imageObject',
            url: linkedDataObj.image
          },
          publisher: {
            '@type': 'Organization',
            name: 'Comic Relief',
            logo: {
              '@type': 'imageObject',
              url: `${siteUrl}assets/logo.jpg`
            }
          }
        })}
      </script>
    )}
  </SEO>
);

export const pageQuery = graphql`
  query getArticle($slug: String, $categories: [String], $type: String) {
    article: allContentfulPageArticle(filter: {path: {eq: $slug}}) {
      edges {
        node {
          path
          title
          displayDate(formatString: "Do MMMM YYYY")
          categories {
            title
          }
          body {
            raw
            references {
              __typename
              ...HtmlCode
              ...Accordion
              ...Asset
            }
          }
          summary {
            summary
          }
          image {
            ...Picture
          }
          body {
            raw
          }
        }
      }
    }
    related: allContentfulPageArticle(
      filter: {type: {eq: $type}, path: {ne: $slug}, categories: {elemMatch: {title: {in: $categories}}}}
      limit: 3
      sort: {displayDate: DESC}
    ) {
      edges {
        node {
          title
          categories {
            title
          }
          path
          type
          id
          body {
            raw
          }
          displayDate(formatString: "Do MMMM YYYY")
          image {
            ...Picture
          }
        }
      }
    }
  }
`;
